import type { ExpeditionStatus as CardStatus } from "~/components/expedition-card";
import type { ExpeditionStatus } from "~/graphql/generated";
import { KIND_LABEL } from "~/lib/expedition-constants";
import { nameInitials } from "~/lib/expedition-utils";
import { fmtDateShort, fmtDays, fmtMiles } from "~/lib/fmt";
import type { ExpeditionListItem, ListTabFilter } from "./expeditions.types";

/**
 * Map backend SCREAMING_SNAKE_CASE status to expedition-card's lowercase status.
 * `FULLY_BOOKED` has no card equivalent — maps to `"open"` (displayed with a badge).
 */
export function toCardStatus(status: ExpeditionStatus): CardStatus {
  if (status === "FULLY_BOOKED") return "open";
  return status.toLowerCase() as CardStatus;
}

/**
 * Map backend status to the list-page tab filter value.
 * Used for tab-count and filter comparison.
 */
export const STATUS_TO_TAB: Record<ExpeditionStatus, Exclude<ListTabFilter, "all">> = {
  ACTIVE: "active",
  OPEN: "open",
  FULLY_BOOKED: "open",
  PLANNING: "planning",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

/** Map an ExpeditionListItem to ExpeditionCard props. */
export function toCardProps(item: ExpeditionListItem) {
  const confirmed = item.crew.filter((m) => m.status === "CONFIRMED").length;
  const MAX_AVATARS = 3;
  const displayed = item.crew.slice(0, MAX_AVATARS);
  const overflow = item.crew.length > MAX_AVATARS ? item.crew.length - MAX_AVATARS : undefined;

  return {
    title: item.name,
    route: item.route?.name ?? "—",
    status: toCardStatus(item.status),
    ...(item.kind ? { type: KIND_LABEL[item.kind] } : {}),
    stats: [
      {
        label: "Departs",
        value: item.departureDate ? fmtDateShort(item.departureDate) : "—",
        muted: item.status === "COMPLETED" || item.status === "CANCELLED",
      },
      {
        label: "Duration",
        value: item.departureDate && item.returnDate ? fmtDays(item.departureDate, item.returnDate) : "—",
        muted: item.status === "COMPLETED" || item.status === "CANCELLED",
      },
      {
        label: "Distance",
        value: item.route?.totalDistanceNm ? fmtMiles(item.route.totalDistanceNm) : "—",
        muted: item.status === "COMPLETED" || item.status === "CANCELLED",
      },
      {
        label: item.status === "COMPLETED" ? "Crew" : "Berths",
        value: `${confirmed} / ${item.maxCrew}`,
        muted: item.status === "COMPLETED" || item.status === "CANCELLED",
      },
    ],
    ...(item.vessels[0] ? { vessel: { name: item.vessels[0].vessel.name } } : {}),
    crew: displayed.map((m) => ({
      initials: nameInitials(m.user.displayName || `${m.user.firstName} ${m.user.lastName}`),
    })),
    ...(overflow != null ? { crewOverflow: overflow } : {}),
  };
}

/** Count crew members with CONFIRMED status. */
export function countConfirmedCrew(crew: Array<{ status: string }>): number {
  return crew.filter((m) => m.status === "CONFIRMED").length;
}

/** Count crew members with PENDING status. */
export function countPendingCrew(crew: Array<{ status: string }>): number {
  return crew.filter((m) => m.status === "PENDING").length;
}
